import React, { Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { urlConfig } from '../config/urlConfig.js';
import Heroabout from '../components/heroabout';
import TestimonialCarousel from '../components/TestimonialCarousel';
import PersistentFlame from '../Popup/PersistentFlame';
import {
  FiEye, FiTarget, FiHeart, FiArrowRight,
  FiSun, FiMoon, FiStar, FiUsers, FiBookOpen, FiCompass,
  FiAlertCircle, FiCheckCircle,
} from 'react-icons/fi';

// Lazy load heavy 3D component
const Celestialscene = lazy(() => import('../components/Celestialscene'));

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <><Helmet>
  {/* Primary SEO */}
  <title>About Andlegur | Mission, Vision, Values & Our Story</title>
  <meta
    name="description"
    content="Learn about Andlegur — a digital sanctuary for emotional well-being, self-reflection, and personal growth. Discover our mission, vision, core values, and the story behind the platform."
  />
  <meta
    name="keywords"
    content="Andlegur about, digital sanctuary mission, emotional well-being platform, personal growth app, guided reflection tools, mindfulness journaling, self-development community, inner clarity, emotional intelligence app, life alignment tools, purpose-driven platform, mental wellness app"
  />

  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta property="og:url" content={urlConfig.app.pages.about} />
  <meta property="og:title" content="About Andlegur | Our Mission, Vision & Community" />
  <meta property="og:description" content="Andlegur is a calm, supportive digital space — built for reflection, emotional clarity, and personal growth. Meet the mission behind the sanctuary." />
  <meta property="og:image" content={urlConfig.assets.ogImage} />
  <meta property="og:image:secure_url" content={urlConfig.assets.ogImage} />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />
  <meta property="og:site_name" content="Andlegur" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:url" content={urlConfig.app.pages.about} />
  <meta name="twitter:title" content="About Andlegur | Our Mission & Vision" />
  <meta name="twitter:description" content="Explore Andlegur's mission to create a calm, supportive digital space for reflection, emotional balance, and personal insight." />
  <meta name="twitter:image" content={urlConfig.assets.ogImage} />

  {/* Technical SEO */}
  <meta name="robots" content="index, follow" />
  <meta name="author" content="Andlegur" />
  <link rel="canonical" href="https://andlegur.com/about" />

  {/* Structured Data — AboutPage + Organization */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "url": "https://andlegur.com/about",
      "name": "About Andlegur",
      "description": "Andlegur is a digital sanctuary designed to support reflection, emotional clarity, personal growth, and meaningful connection.",
      "mainEntity": {
        "@type": "Organization",
        "name": "Andlegur",
        "url": "https://andlegur.com",
        "logo": urlConfig.assets.logo,
        "foundingDate": "2025",
        "description": "Andlegur is a digital sanctuary for emotional well-being, guided reflection, personal growth, and community connection.",
        "mission": "To support individuals in cultivating inner clarity, emotional balance, and purposeful living through reflective tools and guided experiences.",
        "vision": "To become a globally trusted digital sanctuary where people return to reflect, grow, and reconnect with what matters most.",
        "sameAs": [
          "https://x.com/andlegur",
          "https://www.instagram.com/andlegur1/",
          "https://www.youtube.com/@Andlegur",
          "https://www.facebook.com/profile.php?id=61563751566301"
        ]
      }
    })}
  </script>
</Helmet>

      <div className="min-h-screen font-serif text-indigo-100">

        {/* ① Hero — kept as-is */}
        <section>
          <Heroabout />
        </section>

        {/* ② Vision · Mission · Values — 3 fused-image panels side by side */}
        <section aria-label="Andlegur vision mission and values" className="relative py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                label: 'Our Vision',
                Icon: FiEye,
                img: '/web_images/Andlegur%20web%2027.webp',
                accent: 'text-indigo-300',
                border: 'border-indigo-400/40',
                text: 'To become a leading global platform for personal growth and guidance —where people return daily to reflect, grow, and live with greater clarity and alignment.',
              },
              {
                label: 'Our Mission',
                Icon: FiTarget,
                img: '/web_images/Andlegur%20web%2016.webp',
                accent: 'text-violet-300',
                border: 'border-violet-400/40',
                text: 'To guide people toward inner peace, emotional balance, and purposeful living through tools that support reflection, self-awareness, and personal growth.',
              },
              {
                label: 'Our Values',
                Icon: FiHeart,
                img: '/web_images/Andlegur%20web%2022.webp',
                accent: 'text-sky-300',
                border: 'border-sky-400/40',
                values: ['Clarity', 'Peace', 'Compassion', 'Growth', 'Alignment', 'Authenticity', 'Emotional Empowerment'],
              },
            ].map(({ label, Icon, img, accent, border, text, values }) => (
              <div key={label} className={`flex flex-col bg-[#0E1833] border ${border} rounded-2xl group overflow-hidden`}>
                {/* Content — top */}
                <div className="px-7 pt-12 pb-10 flex flex-col flex-1">
                  <div className={`w-10 h-10 rounded-xl border ${border} bg-white/5 flex items-center justify-center mb-5`}>
                    <Icon className={`w-5 h-5 ${accent}`} />
                  </div>
                  <h2 className={`text-xs sm:text-lg uppercase font-semibold tracking-widest mb-3 ${accent}`}>{label}</h2>
                  {text && <p className="text-white text-base leading-relaxed">{text}</p>}
                  {values && (
                    <ul className="space-y-1.5">
                      {values.map(v => (
                        <li key={v} className="flex items-center gap-2 text-indigo-100 text-sm">
                          <span className={`w-1.5 h-1.5 rounded-full bg-current ${accent}`} />
                          {v}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {/* Image — bottom */}
                <div className="relative overflow-hidden h-56 sm:h-64 shrink-0">
                  <img
                    src={img} alt={`Andlegur ${label.toLowerCase()} — ${text || values?.join(', ')}`}
                    className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
                    loading="lazy" width="600" height="400"
                  />
                  {/* Soft corner vignettes only */}
                  <div className="absolute inset-0 bg-linear-to-br from-[#05091B]/50 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-linear-to-tl from-[#05091B]/50 via-transparent to-transparent" />
                </div>
              </div>
            ))}
          </div>
          </div>
        </section>

        {/* CelestialScene wrapper — sections ③ through ⑧ */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 w-full h-full z-0 pointer-events-none overflow-hidden">
            <Suspense fallback={null}>
              <Celestialscene />
            </Suspense>
          </div>

          {/* ③ The Space Between — image right, quote left */}
          <section aria-label="About Andlegur — a space for reflection and growth" className="relative py-20 overflow-hidden">
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
              <div className="grid sm:grid-cols-2 gap-12 items-center">
                {/* Left — text */}
                <div>
                  <p className="text-indigo-400 text-xs uppercase tracking-widest mb-4">You Found the Right Place</p>
                  <h2 className="text-3xl sm:text-5xl font-light text-white mb-6 leading-snug">
                    Overwhelmed, overthinking, or just trying to understand yourself?<br /><span className="text-indigo-300">This is where that changes.</span>
                  </h2>
                  <div className="w-12 h-px bg-indigo-500 mb-6" />
                  <p className="text-indigo-200 text-base leading-relaxed mb-5">
                    Most platforms tell you what to do. Andlegur asks you what you feel. There is no curriculum, no timeline, no performance. Just you, your thoughts, and the clarity that comes when they finally have somewhere to go.
                  </p>
                  <p className="text-indigo-300 text-lg italic mb-8">"Other platforms offer escape. Andlegur offers return."</p>
                  <Link
                    to="/sanctuary/chat"
                    className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    Find Your Clarity <FiArrowRight />
                  </Link>
                </div>
                {/* Right — image with clip + overlay */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: 420 }}>
                  <img
                    src="/web_images/Andlegur%20web%2017.webp"
                    alt="Andlegur digital sanctuary — a space that supports reflection, emotional clarity, and personal growth"
                    className="w-full h-full object-cover"
                    style={{ minHeight: 420 }}
                    loading="lazy" width="640" height="640"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-indigo-950/80 via-transparent to-transparent" />
                  <div className="absolute bottom-8 left-8 right-8">
                    <p className="text-white text-2xl font-light leading-snug">You do not have to figure it all out.<br />You just have to start.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ④ What We're Solving — image below each column */}
          <section aria-label="The problem Andlegur is solving and its solutions" className="relative py-20 overflow-hidden">
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-14">
                <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">The Gap We're Closing</p>
                <h2 className="text-3xl sm:text-5xl lg:text-6xl font-light text-white">
                  The Problem <span className="text-rose-400">&</span> The Solution
                </h2>
                <p className="text-indigo-300 text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  From feeling overwhelmed to feeling reconnected — Andlegur supports your journey from challenge to clarity.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                {/* Problems */}
                <div className="flex flex-col rounded-2xl overflow-hidden border border-rose-500/30">
                  <div className="bg-rose-950/10 p-8 flex-1">
                  <h3 className="text-rose-300 text-sm uppercase tracking-widest mb-6 flex items-center gap-3">
                    <FiAlertCircle className="w-5 h-5 text-rose-200 shrink-0" /> What people are facing
                  </h3>
                  <ul className="space-y-4">
                    {[
                      'People feel emotionally stretched and mentally overwhelmed.',
                      'A constant flood of distractions drowns out inner clarity.',
                      'Most tools focus on entertainment, not reflection.',
                      'Periods of transition often come without meaningful support.',
                      'Information overload leads to uncertainty and decision fatigue.',
                      'Career and life paths can feel unclear or misaligned.',
                      'There is a widespread desire for meaning, balance, and direction.',
                      'Few digital spaces are designed for calm, depth, and presence.',
                    ].map((p, i) => (
                      <li key={i} className="flex items-start gap-3 text-rose-50 text-sm leading-relaxed">
                        <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-rose-200" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  </div>
                  {/* Image below Problems */}
                  <div className="relative h-52 overflow-hidden shrink-0">
                    <img
                      src="/web_images/Andlegur%20web%2015.webp"
                      alt="The emotional challenges many people face — Andlegur addresses overwhelm, disconnection, and lack of inner clarity"
                      className="w-full h-full object-cover"
                      loading="lazy" width="600" height="400"
                    />
                    <div className="absolute inset-0 bg-rose-950/20" />
                  </div>
                </div>
                {/* Solutions */}
                <div className="flex flex-col rounded-2xl overflow-hidden border border-indigo-500/30">
                  <div className="bg-indigo-950/40 p-8 flex-1">
                  <h3 className="text-indigo-300 text-sm uppercase tracking-widest mb-6 flex items-center gap-3">
                    <FiCheckCircle className="w-5 h-5 text-indigo-400 shrink-0" /> What Andlegur offers
                  </h3>
                  <ul className="space-y-4">
                    {[
                      'Thoughtful emotional support, available when you need it.',
                      'Tools that help bring clarity to life decisions and transitions.',
                      'Practices for reflection, grounding, and self-awareness.',
                      'Support for aligning personal values with life and career choices.',
                      'A calm alternative to endless scrolling and surface-level content.',
                      'Insight that helps transform challenges into understanding.',
                      'A global community connected through shared growth and reflection.',
                      'Accessible tools designed for diverse backgrounds, cultures, and paths.',
                    ].map((s, i) => (
                      <li key={i} className="flex items-start gap-3 text-indigo-100 text-sm leading-relaxed">
                        <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400" />
                        {s}
                      </li>
                    ))}
                  </ul>
                  </div>
                  {/* Image below Solutions */}
                  <div className="relative h-52 overflow-hidden shrink-0">
                    <img
                      src="/web_images/Andlegur%20web%2013.webp"
                      alt="What Andlegur offers — tools for reflection, emotional clarity, and purposeful personal growth"
                      className="w-full h-full object-cover"
                      loading="lazy" width="600" height="400"
                    />
                    <div className="absolute inset-0 bg-indigo-950/20" />
                  </div>
                </div>
              </div>
              <div className="text-center mt-10">
                <button
                  onClick={() => navigate('/register')}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  Start Your Reflection Journey Today →
                </button>
              </div>
            </div>
          </section>

          {/* ⑤ Awareness · Healing · Growth — alternating image-left / image-right */}
          <section aria-label="Andlegur pillars — awareness, healing, and growth" className="relative py-20 overflow-hidden">
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-14">
                <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">The Three Pillars</p>
                <h2 className="text-3xl sm:text-4xl font-light text-white">Your Path of Becoming</h2>
              </div>
              <div className="space-y-16">
                {[
                  {
                    moon: '🌑', title: 'Awareness',
                    subtitle: 'Reconnect with what matters beneath the noise.',
                    desc: 'You are more than tasks, deadlines, and constant scrolling. Andlegur offers a supportive space to pause, reflect, and regain clarity — gently, intuitively, and at your own pace.',
                    img: '/web_images/Andlegur%20web%204.webp',
                    alt: 'Andlegur awareness tools — guided reflection and mindfulness practices for emotional clarity',
                    cta: 'Begin the Journey', route: '/sanctuary',
                    flip: false, accent: 'border-indigo-400', tag: 'text-indigo-300',
                  },
                  {
                    moon: '🌓', title: 'Healing',
                    subtitle: 'Turn challenges into power.',
                    desc: 'Emotional experiences don\'t need to be hidden — they can become sources of insight and strength. Andlegur provides reflective tools to support balance, resilience, and emotional well-being.',
                    img: '/web_images/Andlegur%20web%207.webp',
                    alt: 'Andlegur healing journey — emotional support tools for resilience, balance, and inner growth',
                    cta: 'Explore Archetypes', route: '/sanctuary/archetypes',
                    flip: true, accent: 'border-violet-400', tag: 'text-violet-300',
                  },
                  {
                    moon: '🌕', title: 'Growth',
                    subtitle: 'Rise aligned, confident, and whole.',
                    desc: 'This is more than change — it\'s your becoming. Andlegur supports intentional growth, helping you shape a life that feels grounded, meaningful, and sustainable.',
                    img: '/web_images/Andlegur%20web%209.webp',
                    alt: 'Andlegur personal growth — intentional daily practices and guidance for life alignment',
                    cta: 'Start Growing', route: '/sanctuary/membership',
                    flip: false, accent: 'border-teal-400', tag: 'text-teal-300',
                  },
                ].map(({ moon, title, subtitle, desc, img, alt, cta, route, flip, accent, tag }) => (
                  <div key={title} className={`grid sm:grid-cols-2 gap-10 items-center ${flip ? 'sm:grid-flow-dense' : ''}`}>
                    {/* Image */}
                    <div className={`relative rounded-2xl overflow-hidden shadow-2xl ${flip ? 'sm:col-start-2' : ''}`} style={{ minHeight: 360 }}>
                      <img src={img} alt={alt} className="w-full h-full object-cover" style={{ minHeight: 360 }} loading="lazy" width="640" height="480" />
                      <div className="absolute inset-0 bg-linear-to-t from-indigo-950/70 via-transparent to-transparent" />
                      <div className={`absolute top-5 left-5 border ${accent} bg-white/5 backdrop-blur-sm rounded-xl px-4 py-2 text-2xl`}>{moon}</div>
                    </div>
                    {/* Text */}
                    <div className={flip ? 'sm:col-start-1' : ''}>
                      <p className={`text-xs uppercase tracking-widest mb-3 ${tag}`}>{title}</p>
                      <h3 className="text-2xl sm:text-3xl font-light text-white mb-3">{subtitle}</h3>
                      <div className={`w-10 h-px mb-5 ${title === 'Awareness' ? 'bg-indigo-400' : title === 'Healing' ? 'bg-violet-400' : 'bg-teal-400'}`} />
                      <p className="text-indigo-200 text-base leading-relaxed mb-7">{desc}</p>
                      <button
                        onClick={() => navigate(route)}
                        className={`inline-flex items-center gap-2 border ${accent} text-white hover:bg-white/5 px-7 py-3 rounded-full transition-all duration-300 text-sm`}
                      >
                        {cta} <FiArrowRight />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ⑥ About Andlegur — full-bleed image banner + 3 overlaid feature cards */}
          <section aria-label="About Andlegur platform features" className="relative overflow-hidden">
            <div className="relative">
              <img
                src="/web_images/Andlegur%20web%208.webp"
                alt="Andlegur platform — personalized archetypes, guided practices, and reflection tools for personal growth"
                className="w-full object-cover h-80 sm:h-96"
                loading="lazy" width="1200" height="600"
              />
              <div className="absolute inset-0 bg-[#05091B]/75" />
              <div className="absolute inset-0 bg-linear-to-b from-[#05091B] via-transparent to-[#05091B]" />
              <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 -mt-36 pb-16">
                <div className="text-center mb-10">
                  <div className="inline-block w-full max-w-2xl mx-auto bg-indigo-950 backdrop-blur-md border border-indigo-500/80 rounded-3xl px-8 py-10 shadow-2xl">
                    <p className="text-indigo-300 text-xs uppercase tracking-widest mb-3">What We Offer</p>
                    <h2 className="text-3xl sm:text-4xl font-light text-white">About Andlegur</h2>
                    <div className="w-12 h-px bg-indigo-500/60 mx-auto my-5" />
                    <p className="text-indigo-200 text-base sm:text-lg leading-relaxed">
                      Andlegur is more than an app. It's the personal development platform designed to help you understand yourself, navigate challenges, and grow with clarity.
                    </p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-5">
                  {[
                    { Icon: FiCompass, title: 'Personalized Archetypes', desc: 'Reflector, Healer, Liberator, Visionary, Guide, Alchemist — each one a unique path to self-understanding.', color: 'border-indigo-400/50' },
                    { Icon: FiBookOpen, title: 'Guided Practices & Reflections', desc: 'Poetic prompts, seasonal rituals, and voice-guided meditations designed around your becoming.', color: 'border-violet-400/50' },
                    { Icon: FiStar, title: 'No Pressure. No Perfection.', desc: 'Just being — gently held and deeply seen. Andlegur meets you where you are, always.', color: 'border-sky-400/50' },
                  ].map(({ Icon, title, desc, color }) => (
                    <div key={title} className={`bg-[#0E1833]/80 backdrop-blur-md border ${color} rounded-2xl p-7 hover:scale-[1.02] transition-all duration-300`}>
                      <Icon className="w-6 h-6 text-indigo-300 mb-4" />
                      <h3 className="text-white font-semibold mb-2">{title}</h3>
                      <p className="text-indigo-200 text-sm leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-10">
                  <button
                    onClick={() => navigate('/register')}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    Begin Your Journey →
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ⑦ Testimonials */}
          <section className="relative z-10">
            <TestimonialCarousel />
          </section>

          {/* ⑧ Who It's For — 2-col cards each with a top image */}
          <section aria-label="Who Andlegur supports — emotional support, clarity, growth, and life alignment" className="relative py-20 overflow-hidden">
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-12">
                <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">For Everyone Who Reflects</p>
                <h2 className="text-3xl sm:text-4xl font-light text-white">Andlegur's Role in Supporting Growth</h2>
                <p className="text-indigo-300 text-base mt-4 max-w-xl mx-auto">Andlegur is a supportive digital space for reflection, emotional clarity, and personal development.</p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                  { Icon: FiHeart, img: '/web_images/Andlegur%20web%206.webp', alt: 'Andlegur emotional support — grounding practices for stress, burnout, and emotional fatigue', title: 'Emotional Support', points: ['Grounding practices and reflective prompts', 'A calm, supportive presence without judgment', 'Healing as a process of awareness, not fixing'] },
                  { Icon: FiCompass, img: '/web_images/Andlegur%20web%204.webp', alt: 'Andlegur inner guidance — personalized reflections for identity, direction, and personal meaning', title: 'Inner Guidance', points: ['Personalized reflections shaped by your patterns', 'Insight through thoughtful prompts', 'Gentle reminders and emotional check-ins'] },
                  { Icon: FiEye, img: '/web_images/Andlegur%20web%205.webp', alt: 'Andlegur clarity and insight — journaling and pattern recognition for emotional clarity and growth', title: 'Clarity & Insight', points: ['Reflects back insights in a clear, human tone', 'Journaling, pattern recognition, and questioning', 'Uncover direction drawn from your own values'] },
                  { Icon: FiMoon, img: '/web_images/Andlegur%20web%2014.webp', alt: 'Andlegur emotional reflection — affirming language and continuity for those who feel deeply', title: 'Emotional Reflection', points: ['Affirming language and continuity', 'Track emotional rhythms over time', 'A space where inner experiences are respected'] },
                  { Icon: FiTarget, img: '/web_images/Andlegur%20web%2012.jpg', alt: 'Andlegur life and career alignment — decision support and reflective check-ins for purpose', title: 'Life & Career Alignment', points: ['Decision-support prompts and check-ins', 'Align emotional well-being with career direction', 'Creative exploration through structured reflection'] },
                  { Icon: FiSun, img: '/web_images/Andlegur%20web%207.webp', alt: 'Andlegur personal growth — reflective practices for anyone ready to engage with their own development', title: 'Personal Growth', points: ['Come to reflect, not to consume', 'Pause and engage meaningfully', 'Shape your own growth path'] },
                ].map(({ Icon, img, alt, title, points }) => (
                  <div key={title} className="bg-indigo-900/30 border border-indigo-700/40 rounded-2xl overflow-hidden group hover:border-indigo-500/60 transition-all duration-300">
                    <div className="relative h-36 overflow-hidden">
                      <img src={img} alt={alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" width="400" height="200" />
                      <div className="absolute inset-0 bg-linear-to-t from-indigo-950/80 via-indigo-950/30 to-transparent" />
                      <Icon className="absolute bottom-4 left-4 w-5 h-5 text-indigo-300" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-white font-semibold mb-3">{title}</h3>
                      <ul className="space-y-2">
                        {points.map((pt, i) => (
                          <li key={i} className="flex items-start gap-2 text-indigo-200 text-sm leading-relaxed">
                            <span className="mt-1.5 shrink-0 w-1 h-1 rounded-full bg-indigo-400" />
                            {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <button
                  onClick={() => navigate('/register')}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  Start Writing →
                </button>
              </div>
            </div>
          </section>

          {/* ⑨ Platform Gallery — indexed by Google Images */}
          <section aria-label="Andlegur platform screenshots and features gallery" className="relative py-12 overflow-hidden">
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-8">
                <p className="text-indigo-400 text-xs uppercase tracking-widest mb-3">A Closer Look</p>
                <h2 className="text-2xl sm:text-3xl font-light text-white">Inside Andlegur</h2>
              </div>
              {/* Mosaic grid with varying heights */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="col-span-2 sm:col-span-2 row-span-2 relative rounded-2xl overflow-hidden shadow-2xl" style={{ minHeight: 320 }}>
                  <img src="/web_images/Andlegur%20web%202.webp" alt="Andlegur digital sanctuary home experience — reflection and personal growth platform overview" className="w-full h-full object-cover" loading="lazy" width="600" height="600" />
                  <div className="absolute inset-0 bg-linear-to-t from-indigo-950/60 via-transparent to-transparent" />
                  <p className="absolute bottom-5 left-5 text-white text-sm font-light">The Sanctuary</p>
                </div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square">
                  <img src="/web_images/Andlegur%20web%205.webp" alt="Andlegur mission and vision — commitment to emotional well-being and personal clarity" className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
                  <div className="absolute inset-0 bg-indigo-950/30" />
                </div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square">
                  <img src="/web_images/Andlegur%20web%206.webp" alt="Andlegur guided reflection tools — helping users find clarity, balance, and emotional insight" className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
                  <div className="absolute inset-0 bg-indigo-950/30" />
                </div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square">
                  <img src="/web_images/Andlegur%20web%207.webp" alt="Andlegur personal growth features — archetypes and practices for self-discovery and clarity" className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
                  <div className="absolute inset-0 bg-indigo-950/30" />
                </div>
                <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square">
                  <img src="/web_images/Andlegur%20web%208.webp" alt="Andlegur mindfulness and self-development tools — intentional living with AI-guided support" className="w-full h-full object-cover" loading="lazy" width="400" height="400" />
                  <div className="absolute inset-0 bg-indigo-950/30" />
                </div>
              </div>
            </div>
          </section>

        </div>{/* end CelestialScene wrapper */}

        {/* ⑩ Final CTA — full-bleed */}
        <section aria-label="Start your journey with Andlegur" className="relative overflow-hidden py-24">
          <img
            src="/web_images/Andlegur%20web%202.webp"
            alt="Andlegur — begin your reflection journey today"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy" width="1200" height="600"
          />
          <div className="absolute inset-0 bg-[#0E1833]/88" />
          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-indigo-300 text-xs uppercase tracking-widest mb-5">Your Moment Begins Here</p>
            <h2 className="text-3xl sm:text-5xl font-light text-white mb-5 leading-snug">You're Not Lost.<br />You're Becoming.</h2>
            <p className="text-indigo-200 text-base sm:text-lg mb-10 leading-relaxed max-w-xl mx-auto">
              A space to reflect, reconnect, and grow — at your own pace, without pressure, in your own way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/sanctuary/membership')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                Begin Your Aligned Journey →
              </button>
              <button
                onClick={() => navigate('/get-started')}
                className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 rounded-full font-semibold text-lg transition-all duration-300"
              >
                Get the App
              </button>
            </div>
            {/* SEO internal links */}
            <div className="flex flex-wrap justify-center gap-4 mt-10 text-sm">
              {[
                { label: 'Explore Archetypes', to: '/sanctuary/archetypes' },
                { label: 'Guided Practices', to: '/sanctuary/rituals' },
                { label: 'Join the Community', to: '/community' },
                { label: 'Membership Plans', to: '/sanctuary/membership' },
              ].map(({ label, to }) => (
                <Link key={label} to={to} className="text-indigo-300 hover:text-indigo-100 underline underline-offset-4 transition-colors">
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <PersistentFlame />
      </div>
    </>
  );
};

export default AboutPage;